'use client';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import {
  CalendarPlus,
  Calendar,
  Package,
  Users,
  UserPlus,
  Dumbbell,
  MapPin,
  Trophy,
  Star,
  ArrowUpRight,
  type LucideIcon,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

import type { Capability } from '@kit/capabilities';
import { useCapabilityOptional } from '@kit/capabilities/client';

import { pickLocalized } from '~/(tenant)/_components/native/_lib/use-localized';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import type {
  QuickAction,
  QuickActionsSectionConfig,
} from '~/(tenant)/_lib/schema/native-home-config.schema';

const ICON_MAP: Record<string, LucideIcon> = {
  'calendar-plus': CalendarPlus,
  'calendar': Calendar,
  'package': Package,
  'users': Users,
  'user-plus': UserPlus,
  'dumbbell': Dumbbell,
  'map-pin': MapPin,
  'trophy': Trophy,
  'star': Star,
};

/** A tenant-configured href that points outside the app. */
function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/**
 * A safe navigation target: an in-app path or a full http(s) URL. The save-time
 * schema (safeHrefSchema) already rejects dangerous schemes, but this is a
 * render-time backstop so a stray value (legacy data, manual DB edit) degrades
 * to a no-op tile instead of injecting `javascript:`/`data:` into an anchor.
 */
function isSafeHref(href: string): boolean {
  return href.startsWith('/') || isExternalHref(href);
}

/**
 * Renders an internal route via Next.js <Link> (in-app navigation) and an
 * external URL via a plain anchor that opens in a new tab — mirroring the
 * Google-Maps link in location-section.tsx. Lets tenants point a quick action
 * at any external link (e.g. https://wa.me/...) without hijacking the WebView.
 */
function ActionLink({
  href,
  style,
  children,
}: {
  href: string;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  const safeHref = isSafeHref(href) ? href : '/';

  if (isExternalHref(safeHref)) {
    return (
      <a href={safeHref} target="_blank" rel="noopener noreferrer" style={style}>
        {children}
      </a>
    );
  }

  return (
    <Link href={safeHref} style={style}>
      {children}
    </Link>
  );
}

interface QuickActionsSectionProps {
  config: QuickActionsSectionConfig;
  accountId: string;
  storageUrl: string;
  /**
   * The active venue (location-switcher cookie), mirroring HeroSection. Actions
   * tagged with a `locationId` only render when it matches; untagged actions are
   * global. null/undefined = no active venue → only global actions show.
   */
  activeLocationId?: string | null;
}

function resolveImageUrl(storageUrl: string, accountId: string, imageKey: string) {
  return `${storageUrl}/storage/v1/object/public/home_assets/${accountId}/${imageKey}`;
}

/**
 * Editorial quick-actions grid.
 *
 * 2-column tiles, hairline-bordered, neutral surface — matches the rest of
 * the native home (today-classes, coaches, stats-strip). The per-action
 * color from the admin builder is used only as a small accent on the icon
 * stroke and a corner ornament, not as a saturated card fill — keeps the
 * native home calm and editorial regardless of the colors authors pick.
 *
 * When an action has an `imageKey`, it renders as an image-backed card
 * instead of the minimal tile — useful for sport deep links where authors
 * want a richer visual surface.
 */
export function QuickActionsSection({
  config,
  accountId,
  storageUrl,
  activeLocationId = null,
}: QuickActionsSectionProps) {
  const t = useNativeTokens();
  const { t: translate, i18n } = useTranslation();
  const locale = i18n.language;
  const guestPassesEnabled = useCapabilityOptional('guest_passes' as Capability);
  const memberQrEnabled = useCapabilityOptional(
    'member_qr_checkin' as Capability,
  );
  const entitlementsEnabled = useCapabilityOptional(
    'entitlements' as Capability,
  );
  const tourBookingEnabled = useCapabilityOptional(
    'tour_booking' as Capability,
  );

  if (!config.actions || config.actions.length === 0) return null;

  // Runtime fallback: if the tenant has GUEST_PASSES enabled but no quick
  // action targets `guest_passes`, append a default "Invite a guest" tile so
  // members never lose the path to the invite flow even when the tenant
  // hasn't customized their quick-actions yet. Tenants who configure their
  // own list still have full control — this only fires when the configured
  // list lacks a guest-passes-gated entry.
  const hasConfiguredGuestPassAction = config.actions.some(
    (action) => action.requiresCapability === 'guest_passes',
  );

  const effectiveActions: QuickAction[] =
    guestPassesEnabled && !hasConfiguredGuestPassAction
      ? [
          ...config.actions,
          {
            label: translate('common:nativeHome.guestAllowance.cta'),
            icon: 'user-plus',
            href: '/guests/new',
            color: '',
            requiresCapability: 'guest_passes' as const,
          },
        ]
      : config.actions;

  // Filter capability-gated actions. Configs without `requiresCapability`
  // always render. The schema only allows known gate keys, but we use a
  // local map so unknown values get dropped if the schema ever loosens.
  const visibleActions = effectiveActions.filter((action) => {
    // Per-venue scoping (mirrors hero slides in hero-section.tsx): an action
    // tagged with a `locationId` only shows when it matches the active venue.
    // Untagged actions are global. No active venue → only global actions show.
    if (
      action.locationId &&
      !(activeLocationId != null && action.locationId === activeLocationId)
    ) {
      return false;
    }

    if (!action.requiresCapability) return true;
    switch (action.requiresCapability) {
      case 'guest_passes':
        return guestPassesEnabled;
      case 'member_qr_checkin':
        return memberQrEnabled;
      case 'entitlements':
        return entitlementsEnabled;
      case 'tour_booking':
        return tourBookingEnabled;
      default:
        return false;
    }
  });

  if (visibleActions.length === 0) return null;

  return (
    <section style={{ paddingTop: 32, paddingInline: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 12,
        }}
      >
        {visibleActions.map((action, index) => {
          const label =
            pickLocalized(action.label, action.label_ar, locale) ?? action.label;
          const description = pickLocalized(
            action.description,
            action.description_ar,
            locale,
          );

          if (action.imageKey) {
            return (
              <ImageCard
                key={index}
                action={action}
                label={label}
                description={description}
                imageUrl={resolveImageUrl(storageUrl, accountId, action.imageKey)}
                tokens={t}
              />
            );
          }

          return (
            <IconTile
              key={index}
              action={action}
              label={label}
              description={description}
              tokens={t}
            />
          );
        })}
      </div>
    </section>
  );
}

function IconTile({
  action,
  label,
  description,
  tokens: t,
}: {
  action: QuickAction;
  label: string;
  description: string | undefined;
  tokens: ReturnType<typeof useNativeTokens>;
}) {
  const Icon = ICON_MAP[action.icon] ?? CalendarPlus;
  const accent = action.color || t.foreground;

  return (
    <ActionLink
      href={action.href}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 18,
        minHeight: 120,
        padding: 16,
        borderRadius: t.radius,
        border: `1px solid ${t.line}`,
        backgroundColor: t.background,
        color: t.foreground,
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Icon size={22} strokeWidth={1.4} color={accent} aria-hidden />
        <ArrowUpRight size={14} strokeWidth={1.4} color={t.muted} aria-hidden />
      </div>

      <div>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 17,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.005em',
          }}
        >
          {label}
        </div>
        {description && (
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: t.muted,
              marginTop: 6,
            }}
          >
            {description}
          </div>
        )}
      </div>

      <div
        style={{
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 0,
          height: 2,
          backgroundColor: accent,
          opacity: 0.85,
          borderTopLeftRadius: t.radius,
          borderTopRightRadius: t.radius,
        }}
        aria-hidden
      />
    </ActionLink>
  );
}

/**
 * Image-backed card. Photo fills the tile, a soft bottom-up gradient anchors
 * the label, and the author's accent color shows as a thin top stripe so the
 * brand cue is preserved even when the photo dominates.
 */
function ImageCard({
  action,
  label,
  description,
  imageUrl,
  tokens: t,
}: {
  action: QuickAction;
  label: string;
  description: string | undefined;
  imageUrl: string;
  tokens: ReturnType<typeof useNativeTokens>;
}) {
  const accent = action.color || t.foreground;

  return (
    <ActionLink
      href={action.href}
      style={{
        position: 'relative',
        display: 'block',
        minHeight: 140,
        borderRadius: t.radius,
        border: `1px solid ${t.line}`,
        overflow: 'hidden',
        textDecoration: 'none',
        color: '#fff',
        isolation: 'isolate',
      }}
    >
      <Image
        src={imageUrl}
        alt={label}
        fill
        sizes="(max-width: 768px) 50vw, 240px"
        style={{ objectFit: 'cover' }}
      />

      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 75%)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 999,
          backgroundColor: 'rgba(255,255,255,0.85)',
          color: '#111',
        }}
      >
        <ArrowUpRight size={14} strokeWidth={1.6} aria-hidden />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 14,
          right: 14,
          bottom: 12,
        }}
      >
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 17,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.005em',
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          }}
        >
          {label}
        </div>
        {description && (
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              opacity: 0.85,
              marginTop: 4,
            }}
          >
            {description}
          </div>
        )}
      </div>

      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          backgroundColor: accent,
          opacity: 0.95,
        }}
      />
    </ActionLink>
  );
}
